/**
 * Stack utility for managing navigation page history
 * Used to track user navigation flow and enable conditional UI based on previous page
 */
class PageStack {
  private stack: string[] = [];

  /**
   * Push a page name onto the stack
   * @param pageName - The name of the page to push
   */
  push(pageName: string): void {
    if (pageName) {
      this.stack.push(pageName);
    }
  }

  /**
   * Pop the top page name from the stack
   * @returns The popped page name or undefined if stack is empty
   */
  pop(): string | undefined {
    return this.stack.pop();
  }

  /**
   * Get the top page name without removing it
   * @returns The top page name or undefined if stack is empty
   */
  peek(): string | undefined {
    return this.stack.length > 0
      ? this.stack[this.stack.length - 1]
      : undefined;
  }

  /**
   * Get the previous page name (second from top) without removing it
   * @returns The previous page name or undefined if stack has less than 2 items
   */
  getPreviousPage(): string | undefined {
    return this.stack.length >= 2
      ? this.stack[this.stack.length - 2]
      : undefined;
  }

  /**
   * Get the current size of the stack
   * @returns The number of pages in the stack
   */
  size(): number {
    return this.stack.length;
  }

  /**
   * Check if the stack is empty
   * @returns True if stack is empty, false otherwise
   */
  isEmpty(): boolean {
    return this.stack.length === 0;
  }

  /**
   * Clear all pages from the stack
   */
  clear(): void {
    this.stack = [];
  }

  /**
   * Get all pages in the stack (for debugging)
   * @returns A copy of the stack array
   */
  getAllPages(): string[] {
    return [...this.stack];
  }
}

// Export a singleton instance
export const pageStack = new PageStack();
